// Shared error sanitizer (LitigAI implementation; SeriesAI/in-house assistant-message
// helpers appended 2026-10-07). Depends only on ./humanizeError.
import { humanizeErrorMessage } from "./humanizeError";

export const SUPPORT_CONTACT_EMAIL = "vienna@nougat.law";

const buildDefaultSafeMessage = (supportEmail = SUPPORT_CONTACT_EMAIL) =>
  `There was an error processing your request. Please contact ${supportEmail}.`;

const normalizeCandidateMessage = (input) => {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Error) {
    return input.message || "";
  }

  if (input && typeof input === "object" && typeof input.message === "string") {
    return input.message;
  }

  return "";
};

const isSensitiveOpenAIQuotaMessage = (message) => {
  if (typeof message !== "string") {
    return false;
  }

  const normalized = message.toLowerCase();

  if (normalized.includes("you exceeded your current quota")) {
    return true;
  }

  if (normalized.includes("check your plan and billing details")) {
    return true;
  }

  if (normalized.includes("platform.openai.com/docs")) {
    return true;
  }

  if (normalized.includes("platform.openai.com")) {
    return true;
  }

  if (/\binsufficient_quota\b/i.test(message)) {
    return true;
  }

  if (normalized.includes("billing hard limit")) {
    return true;
  }

  if (normalized.includes("billing limit")) {
    return true;
  }

  if (normalized.includes("server was temporarily limiting")) {
    return true;
  }

  if (normalized.includes("rate_limit")) {
    return true;
  }

  if (
    /\brate[\s-]+limit\b/i.test(message) &&
    /\b(openai|provider|upstream|error|failed|429|service|throttl|reached|exceeded|temporarily)\b/i.test(message)
  ) {
    return true;
  }

  if (
    normalized.includes("openai") &&
    (normalized.includes("quota") ||
      normalized.includes("billing") ||
      normalized.includes("plan") ||
      normalized.includes("limit"))
  ) {
    return true;
  }

  return false;
};

/**
 * Returns a user-safe string. For known upstream OpenAI quota/billing errors,
 * the returned message is replaced with a support-contact prompt.
 */
export const sanitizeUserFacingErrorMessage = (input, options = {}) => {
  const customSafeMessage =
    typeof options.safeMessage === "string" && options.safeMessage.trim()
      ? options.safeMessage.trim()
      : null;
  const supportEmail =
    typeof options.supportEmail === "string" && options.supportEmail.trim()
      ? options.supportEmail.trim()
      : SUPPORT_CONTACT_EMAIL;
  const safeMessage =
    customSafeMessage
      ? customSafeMessage
      : buildDefaultSafeMessage(supportEmail);

  const candidate = normalizeCandidateMessage(input);
  const trimmedCandidate = typeof candidate === "string" ? candidate.trim() : "";

  if (!trimmedCandidate) {
    return safeMessage;
  }

  if (isSensitiveOpenAIQuotaMessage(trimmedCandidate)) {
    if (customSafeMessage) return safeMessage;
    const normalized = trimmedCandidate.toLowerCase();
    if (/rate[\s_-]?limit|temporarily limiting|too many requests/.test(normalized)) {
      return "The assistant is busy right now. Please try again in a moment.";
    }
    return "AI assistance is temporarily unavailable. Please try again later.";
  }

  // Toasts are handed raw axios/DRF/Cognito strings all over the app. Rewrite
  // what we recognize, and fall back to the support message rather than
  // showing a developer-facing string like
  // "Request failed with status code 400".
  return humanizeErrorMessage(trimmedCandidate) || safeMessage;
};

// The chat backend deliberately passes a failed run's upstream error through as
// the ASSISTANT MESSAGE TEXT (see django-hub `chat/runs/<id>/status`, which
// returns `responseMessage.text` = "An error occurred while processing the
// request: ..."). It therefore never reaches the SSE `event: error` path that
// sanitizeUserFacingErrorMessage already guards, and renders in the transcript
// as if the assistant had said it.
//
// Measured live 2026-08-15 on seriesai.co: a founder asking about their own
// documents was shown
//   "An error occurred while processing the request: 429 You have no credits
//    remaining. Add credits to continue using the API at
//    https://platform.openai.com/settings/organization/billing/"
// plus a LangChain MODEL_RATE_LIMIT troubleshooting link. That names the vendor,
// leaks internal plumbing, and tells a paying customer to go top up an account
// that is not theirs.
//
// Deliberately keyed on the unambiguous upstream preamble rather than reusing
// the broad quota heuristic: an assistant answering a genuine question about,
// say, API rate limits must not have its answer replaced.
const UPSTREAM_RUN_ERROR_PREFIX = /^\s*an error occurred while processing the request\b/i;

export const isUpstreamRunErrorText = (text) =>
  typeof text === "string" && UPSTREAM_RUN_ERROR_PREFIX.test(text);

/**
 * Returns assistant-message text safe to render. Upstream run errors become the
 * support-contact message; every other answer is returned untouched.
 */
export const sanitizeAssistantMessageText = (text, options = {}) =>
  isUpstreamRunErrorText(text) ? sanitizeUserFacingErrorMessage(text, options) : text;
